/**
 * @file: useViewReportWithTaskContext.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { ActionButtonProps } from '@euler/app/components/BottomAction';
import type { ReportNavParams } from '@euler/app/flows/report/ReportScreens';
import { ReportRef } from '@euler/app/flows/report/functions';
import { reportTabKeyFromScreen } from '@euler/app/flows/report/functions/util';
import type { TaskNavParams } from '@euler/app/flows/task/TaskFlow';
import { useNavigateToTaskDetail } from '@euler/app/flows/task/functions/useNavigateToTaskDetail';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { ShareReportArgs } from '@euler/functions/sharing/types';
import { useAppReviewTracking } from '@euler/functions/useAppReviewTracking';
import { useAlert } from '@euler/functions/ux/useAlert';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { HttpError } from '@euler/lib/request';
import { VehicleInspectionFlow } from '@euler/model';
import { FaultInspectionItem } from '@euler/model/FaultInspectionItem';
import {
  CommonTaskStatus,
  InspectionTaskJobStatus,
  InspectionTaskStatus,
  SamplingCheckResultType,
} from '@euler/model/enum';
import { TaskDetail } from '@euler/model/task-detail';
import { Maybe } from '@euler/types';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { uniq } from 'ramda';
import { useRef } from 'react';
import { TouchableOpacity } from 'react-native';
import { useShareReport } from '../../../../functions/sharing/useShareReport';
import { TaskContextProps, useTaskContext } from './TaskContext';
import { flowStatusToCommonTaskStatus } from './flowStatusToCommonTaskStatus';
import { TaskManager } from './task-detail/TaskManager';

export type ReportScreenType = keyof ReportNavParams;

type Navigation = StackNavigationProp<TaskNavParams>;
type AdditionalParams = Record<string, any>;

type CommonReportScreenProps = {
  reportRef?: ReportRef;
  title?: string;
  accessoryText?: string;
  finishButtonText?: string;
  primaryButton?: Omit<ActionButtonProps, 'onPress'> & {
    onPress: (context: { finish: (params?: any) => Promise<void> }) => void;
  };
  secondaryButton?: Omit<ActionButtonProps, 'onPress'> & {
    onPress: (context: { finish: (params?: any) => Promise<void> }) => void;
  };
  getAdditionalFinishParams?: () => AdditionalParams;
  onFinish?: (additionalParams?: AdditionalParams) => Promise<void>;
  onFinishSuccess?: (args: {
    onShare: () => void;
    additionalParams?: AdditionalParams;
  }) => any;
  onFinishError?: (error: Error) => any;
  canFinish?: () => boolean;
  /**
   * Runs only when the finish button is pressed, so it may surface an alert
   * telling the user what is still missing. Unlike `canFinish`, which is also
   * evaluated when the report screen is opened (and hides the button when it
   * returns false), this leaves the button in place.
   */
  validateFinish?: () => boolean;
  canEdit?: () => boolean;
  canShare?: () => boolean;
};

export type ViewReportRequestParams = {
  preInspectionReport: CommonReportScreenProps;
  inspectionReport: CommonReportScreenProps & {
    flowId?: string;
    faultLikeSubject?: FaultInspectionItem;
    onCancel?: (flowId: string) => void;
  };
  constructionReport: CommonReportScreenProps & {
    targetJobId?: number;
    faultLikeSubject?: FaultInspectionItem;
  };
  deliveryCheckReport: CommonReportScreenProps;
  samplingCheckReport: CommonReportScreenProps;
};

interface ReportTraitsProvider {
  readonly type: ReportScreenType;
  get params(): CommonReportScreenProps;
  getStatus(detail: TaskDetail): CommonTaskStatus;
  canShare(detail: TaskDetail, taskContext: TaskContextProps): boolean;
  canEdit(detail: TaskDetail, taskContext: TaskContextProps): boolean;
  canFinish(detail: TaskDetail, taskContext: TaskContextProps): boolean;
  getExtraParams(): Record<string, any> | undefined;
  allowsEdit(taskContext: TaskContextProps): boolean;
  getAccessoryText(detail: TaskDetail): string | undefined;
  getTitle(detail: TaskDetail): string | undefined;
  getFinishConfirmTitle(
    detail: TaskDetail,
    taskContext: TaskContextProps,
    additionalParams?: AdditionalParams,
  ): string;
  getFinishConfirmMessage(
    detail: TaskDetail,
    taskContext: TaskContextProps,
    additionalParams?: AdditionalParams,
  ): string;
  requestEditReport(
    taskManager: TaskManager,
    navigation: Navigation,
    detail: TaskDetail,
  ): Promise<void>;
  finish(
    detail: TaskDetail,
    taskContext: TaskContextProps,
    additionalParams?: AdditionalParams,
  ): Promise<void>;
}

abstract class ReportTraitsProviderBase<T extends ReportScreenType>
  implements ReportTraitsProvider
{
  constructor(
    readonly type: T,
    readonly params: ViewReportRequestParams[T],
  ) {}

  canShare(detail: TaskDetail, _taskContext: TaskContextProps): boolean {
    return (
      (this.params.canShare == null || this.params.canShare()) &&
      this.getStatus(detail) === CommonTaskStatus.Finished
    );
  }

  allowsEdit(_taskContext: TaskContextProps): boolean {
    return true;
  }

  canEdit(detail: TaskDetail, taskContext: TaskContextProps): boolean {
    return (
      (this.params.canEdit == null || this.params.canEdit()) &&
      this.allowsEdit(taskContext) &&
      detail.basicInfo.status !== InspectionTaskStatus.Finished &&
      detail.basicInfo.status !== InspectionTaskStatus.Cancelled &&
      this.getStatus(detail) === CommonTaskStatus.Finished
    );
  }

  canFinish(detail: TaskDetail, _taskContext: TaskContextProps): boolean {
    return (
      (this.params.canFinish == null || this.params.canFinish()) &&
      detail.basicInfo.status !== InspectionTaskStatus.Finished &&
      detail.basicInfo.status !== InspectionTaskStatus.Cancelled &&
      this.getStatus(detail) === CommonTaskStatus.InProgress
    );
  }

  getExtraParams(): Record<string, any> | undefined {
    return undefined;
  }

  getAccessoryText(_detail: TaskDetail): string | undefined {
    return undefined;
  }

  getTitle(_detail: TaskDetail): string | undefined {
    return undefined;
  }

  getFinishConfirmTitle(
    _detail: TaskDetail,
    _taskContext: TaskContextProps,
    _additionalParams?: AdditionalParams,
  ): string {
    return '提示';
  }

  getFinishConfirmMessage(
    _detail: TaskDetail,
    _taskContext: TaskContextProps,
    _additionalParams?: AdditionalParams,
  ): string {
    return '您即将推送报告给车主，是否继续?';
  }

  async finish(
    detail: TaskDetail,
    taskContext: TaskContextProps,
    additionalParams?: AdditionalParams,
  ): Promise<void> {
    if (!this.canFinish(detail, taskContext)) {
      throw new Error('Cannot finish in this context');
    }
    if (this.params?.onFinish == null) {
      throw new Error('params.onFinish is required to finish this context');
    }
    await this.params.onFinish(additionalParams);
  }

  abstract getStatus(detail: TaskDetail): CommonTaskStatus;

  abstract requestEditReport(
    taskManager: TaskManager,
    navigation: Navigation,
    detail: TaskDetail,
  ): Promise<void>;
}

class PreInspectionTraitsProvider extends ReportTraitsProviderBase<'preInspectionReport'> {
  constructor(params: ViewReportRequestParams['preInspectionReport']) {
    super('preInspectionReport', params);
  }
  getStatus(detail: TaskDetail): CommonTaskStatus {
    return detail.preInspection.status;
  }
  getAccessoryText(detail: TaskDetail): string | undefined {
    return `服务顾问: ${detail.basicInfo.serviceAgentName}`;
  }
  getFinishConfirmMessage(_detail: TaskDetail): string {
    return '您即将完成预检并推送报告给车主，是否继续?';
  }
  async requestEditReport(
    taskManager: TaskManager,
    navigation: Navigation,
    _detail: TaskDetail,
  ): Promise<void> {
    await taskManager.preInspectionManager.requestAmendSiteInspections();
    navigation.navigate('taskDetail', {});
    navigation.push('preInspection', {});
  }
}

class InspectionTraitsProvider extends ReportTraitsProviderBase<'inspectionReport'> {
  constructor(params: ViewReportRequestParams['inspectionReport']) {
    super('inspectionReport', params);
  }
  getTargetFlow(detail: TaskDetail): VehicleInspectionFlow | undefined {
    return detail.inspection.flows.find(x => x.id === this.params?.flowId);
  }
  getStatus(detail: TaskDetail): CommonTaskStatus {
    const flowId = this.params?.flowId;
    if (flowId == null) {
      return detail.inspection.status;
    }
    const flow = detail.inspection.flows.find(x => x.id === flowId);
    if (flow == null) {
      return detail.inspection.status;
    }
    return flowStatusToCommonTaskStatus(flow.status);
  }
  getExtraParams(): Record<string, any> | undefined {
    return {
      flowId: this.params.flowId,
      faultLikeSubject: this.params.faultLikeSubject,
    };
  }
  canEdit(detail: TaskDetail, taskContext: TaskContextProps): boolean {
    return this.params?.flowId != null && super.canEdit(detail, taskContext);
  }
  canFinish(detail: TaskDetail, taskContext: TaskContextProps): boolean {
    const flowId = this.params?.flowId;
    if (flowId != null) {
      return super.canFinish(detail, taskContext);
    }
    return (
      detail.inspection.status !== CommonTaskStatus.Finished &&
      detail.inspection.flows.length > 0 &&
      detail.inspection.flows.every(x => x.status === 'finished')
    );
  }
  getAccessoryText(detail: TaskDetail): string | undefined {
    let technicianName: string | undefined = undefined;
    const flowId = this.params?.flowId;
    if (flowId != null) {
      technicianName = this.getTargetFlow(detail)?.assignedTo.name;
    } else {
      const techs = uniq(detail.inspection.flows.map(x => x.assignedTo.name));
      technicianName = techs.length === 1 ? techs[0] : undefined;
    }
    return technicianName ? `检测技师: ${technicianName}` : undefined;
  }
  getFinishConfirmMessage(_detail: TaskDetail): string {
    return this.params?.flowId != null
      ? '是否确认完成当前检测流程并推送报告给车主?'
      : '是否确认完成全部检测流程并推送报告给车主?';
  }
  async requestEditReport(
    taskManager: TaskManager,
    navigation: Navigation,
    detail: TaskDetail,
  ): Promise<void> {
    const flowId = this.params?.flowId;
    if (flowId == null) {
      throw new Error(
        'Cannot request edit inspection if flowId is not specified',
      );
    }
    const flow = this.getTargetFlow(detail);
    if (flow == null) {
      return;
    }
    const onCancel = this.params?.onCancel;
    if (onCancel == null) {
      throw new Error('params.onCancel is required for edit inspection flow');
    }
    await taskManager.inspectionManager.requestAmendInspectionFlow(flowId);
    navigation.navigate('taskDetail', {});
    navigation.push('inspection', { flowId, onCancel });
  }
}

class ConstructionTraitsProvider extends ReportTraitsProviderBase<'constructionReport'> {
  constructor(params: ViewReportRequestParams['constructionReport']) {
    super('constructionReport', params);
  }
  getStatus(detail: TaskDetail): CommonTaskStatus {
    return detail.construction.status;
  }
  getExtraParams(): Record<string, any> | undefined {
    return {
      targetJobId: this.params.targetJobId,
      faultLikeSubject: this.params.faultLikeSubject,
    };
  }
  canShare(detail: TaskDetail): boolean {
    return (
      (this.params.canShare == null || this.params.canShare()) &&
      detail.construction.jobs.length > 0
    );
  }
  getFinishConfirmMessage(_detail: TaskDetail): string {
    return '您即将完成施工并推送报告给车主，是否继续?';
  }
  canFinish(detail: TaskDetail, taskContext: TaskContextProps): boolean {
    return (
      super.canFinish(detail, taskContext) &&
      detail.construction.jobs.length > 0 &&
      detail.construction.jobs.every(
        x => x.status === InspectionTaskJobStatus.Finished,
      )
    );
  }
  async requestEditReport(
    taskManager: TaskManager,
    navigation: Navigation,
  ): Promise<void> {
    await taskManager.constructionManager.requestAmend();
    navigation.navigate('taskDetail', {});
  }
}

class DeliveryCheckTraitsProvider extends ReportTraitsProviderBase<'deliveryCheckReport'> {
  constructor(params: ViewReportRequestParams['deliveryCheckReport']) {
    super('deliveryCheckReport', params);
  }
  getStatus(detail: TaskDetail): CommonTaskStatus {
    return detail.deliveryCheck.status;
  }
  allowsEdit(taskContext: TaskContextProps): boolean {
    return taskContext.profile.store.isIndependentDeliveryCheckEnabled
      ? true
      : false;
  }
  getFinishConfirmMessage(
    detail: TaskDetail,
    taskContext: TaskContextProps,
  ): string {
    const { profile } = taskContext;
    if (profile.store.isIndependentDeliveryCheckEnabled) {
      return '您即将完成竣工检验，是否继续?';
    } else if (detail.basicInfo.isMainOrder) {
      return '您即将完成交车质检并推送报告给车主，是否继续?';
    } else {
      return '您即将完成子任务，是否继续?';
    }
  }
  getAccessoryText(detail: TaskDetail): string | undefined {
    return detail.deliveryCheck.finishedBy != null
      ? `质检员: ${detail.deliveryCheck.finishedBy}`
      : undefined;
  }
  canFinish(detail: TaskDetail, context: TaskContextProps): boolean {
    return (
      (this.params.canFinish == null || this.params.canFinish()) &&
      detail.basicInfo.status !== InspectionTaskStatus.Finished &&
      detail.basicInfo.status !== InspectionTaskStatus.Cancelled &&
      detail.preInspection.status !== CommonTaskStatus.InProgress &&
      detail.inspection.status !== CommonTaskStatus.InProgress &&
      detail.construction.status !== CommonTaskStatus.InProgress &&
      (!context.profile.store.isIndependentDeliveryCheckEnabled ||
        detail.deliveryCheck.status === CommonTaskStatus.InProgress)
    );
  }
  async requestEditReport(
    taskManager: TaskManager,
    navigation: Navigation,
  ): Promise<void> {
    if (!taskManager.currentUser.store.isIndependentDeliveryCheckEnabled) {
      throw new Error('edit of delivery check report is not allowed');
    }
    await taskManager.deliveryCheckManager.requestAmend();
    navigation.navigate('taskDetail', {});
    await taskManager.deliveryCheckManager.prepare();
    navigation.push('_deliveryCheck', {});
  }
}

class SamplingCheckTraitsProvider extends ReportTraitsProviderBase<'samplingCheckReport'> {
  constructor(params: ViewReportRequestParams['samplingCheckReport']) {
    super('samplingCheckReport', params);
  }
  getStatus(detail: TaskDetail): CommonTaskStatus {
    return detail.samplingCheck.status;
  }
  canShare(): boolean {
    return false;
  }

  canEdit(detail: TaskDetail, taskContext: TaskContextProps): boolean {
    return Boolean(
      (this.params.canEdit == null || this.params.canEdit()) &&
        this.allowsEdit(taskContext) &&
        (detail.basicInfo.status !== InspectionTaskStatus.Finished ||
          taskContext.storeConfiguration.allowSamplingCheckAfterFinish) &&
        detail.basicInfo.status !== InspectionTaskStatus.Cancelled &&
        this.getStatus(detail) === CommonTaskStatus.Finished,
    );
  }

  allowsEdit(_taskContext: TaskContextProps): boolean {
    return true;
  }
  getFinishConfirmTitle(
    detail: TaskDetail,
    _taskContext: TaskContextProps,
    additionalParams?: { resultType?: SamplingCheckResultType },
  ): string {
    if (detail.basicInfo.status === InspectionTaskStatus.Finished) {
      if (additionalParams?.resultType === SamplingCheckResultType.Failed) {
        return '抽验不合格';
      } else {
        return '抽验合格';
      }
    }
    return '提示';
  }
  getFinishConfirmMessage(
    detail: TaskDetail,
    _taskContext: TaskContextProps,
    additionalParams?: { resultType?: SamplingCheckResultType },
  ): string {
    if (detail.basicInfo.status === InspectionTaskStatus.Finished) {
      return '是否提交并完成抽检?';
    }
    if (additionalParams?.resultType === SamplingCheckResultType.Failed) {
      return '您即将完成完工抽检，并禁止交车，是否继续?';
    } else {
      return '您即将完成完工抽检，并允许交车，是否继续?';
    }
  }
  getAccessoryText(detail: TaskDetail): string | undefined {
    return detail.samplingCheck.startedBy != null ||
      detail.samplingCheck.finishedBy != null
      ? `质检员: ${
          detail.samplingCheck.finishedBy ?? detail.samplingCheck.startedBy
        }`
      : undefined;
  }
  canFinish(detail: TaskDetail, context: TaskContextProps): boolean {
    return Boolean(
      (detail.basicInfo.status !== InspectionTaskStatus.Finished ||
        context.storeConfiguration.allowSamplingCheckAfterFinish) &&
        detail.basicInfo.status !== InspectionTaskStatus.Cancelled &&
        detail.preInspection.status !== CommonTaskStatus.InProgress &&
        detail.inspection.status !== CommonTaskStatus.InProgress &&
        detail.construction.status !== CommonTaskStatus.InProgress &&
        detail.deliveryCheck.status === CommonTaskStatus.Finished &&
        detail.samplingCheck.status === CommonTaskStatus.InProgress,
    );
  }
  async requestEditReport(
    taskManager: TaskManager,
    navigation: Navigation,
  ): Promise<void> {
    await taskManager.samplingCheckManager.requestAmend();
    navigation.navigate('taskDetail', {});
    await taskManager.samplingCheckManager.prepare();
    navigation.push('_samplingCheck', {});
  }
}

const TraitsProviderFactory: {
  [p in ReportScreenType]: (
    params: ViewReportRequestParams[p],
  ) => ReportTraitsProvider;
} = {
  preInspectionReport: params => new PreInspectionTraitsProvider(params),
  inspectionReport: params => new InspectionTraitsProvider(params),
  constructionReport: params => new ConstructionTraitsProvider(params),
  deliveryCheckReport: params => new DeliveryCheckTraitsProvider(params),
  samplingCheckReport: params => new SamplingCheckTraitsProvider(params),
};

function getTraitsProvider<T extends ReportScreenType>(
  type: T,
  params: ViewReportRequestParams[T],
): ReportTraitsProvider {
  return TraitsProviderFactory[type](params);
}

export const useViewReportWithTaskContext = () => {
  const navigation = useNavigation<StackNavigationProp<TaskNavParams>>();
  const trackAppReview = useAppReviewTracking();
  const appLoading = useAppLoading();
  const isMobile = useIsMobileLayout();
  const alert = useAlert();
  const shareButtonRef = useRef<TouchableOpacity>(null);
  const taskContext = useTaskContext();
  const { detail, taskManager, reportTarget } = taskContext;
  const confirm = useConfirm();

  const { shareReportToCustomer } = useShareReport();
  const navigateToTaskDetail = useNavigateToTaskDetail();
  const share = usePersistFn(async (screen: Maybe<ReportScreenType>) => {
    const args: ShareReportArgs = {
      target: { type: reportTarget.type, id: reportTarget.id },
      licensePlateNo: reportTarget.licensePlateNo,
      orderNo: reportTarget.orderNo,
      reportNo: reportTarget.reportNo,
      tab: screen ? reportTabKeyFromScreen(screen) : undefined,
      shareToken: taskContext.taskManager.extraInfo$.value?.shareToken,
      success: () => {
        trackAppReview();
      },
    };
    await shareReportToCustomer(args);
  });

  const edit = usePersistFn(async (traits: ReportTraitsProvider) => {
    const confirmed = await confirm({
      title:
        traits.type === 'samplingCheckReport' ? '是否确认修改报告?' : '确认',
      message:
        traits.type === 'samplingCheckReport'
          ? undefined
          : '报告已推送给车主，是否确认修改?',
      confirmButtonText: '确认',
      cancelButtonText: '取消',
    });
    if (!confirmed) {
      return;
    }
    await appLoading.showWithAction(async () => {
      await traits.requestEditReport(taskManager, navigation, detail);
    });
  });

  const finish = usePersistFn(
    async (
      traits: ReportTraitsProvider,
      additionalParams?: AdditionalParams,
    ) => {
      if (traits.params.canFinish != null && !traits.params.canFinish()) {
        return;
      }

      if (
        traits.params.validateFinish != null &&
        !traits.params.validateFinish()
      ) {
        return;
      }

      const confirmed = await confirm({
        title: traits.getFinishConfirmTitle(
          detail,
          taskContext,
          additionalParams,
        ),
        message: traits.getFinishConfirmMessage(
          detail,
          taskContext,
          additionalParams,
        ),
        confirmButtonText: '确认',
        cancelButtonText: '取消',
      });

      if (!confirmed) {
        return;
      }

      await appLoading.showWithAction(
        async () => {
          await traits.finish(detail, taskContext, additionalParams);
          if (traits.params.onFinishSuccess != null) {
            traits.params.onFinishSuccess({
              additionalParams,
              onShare: () => {
                void share(traits.type);
              },
            });
            return;
          }

          navigation.replace('_hooray', {
            type: 'success',
            soundType: 'chirp',
            buttonText: '返回任务首页',
            onPress: () => {
              navigateToTaskDetail();
              trackAppReview();
            },
            onShare: () => void share(traits.type),
          });
        },
        {
          onError: async e => {
            let res = traits.params.onFinishError?.(e);
            if (res != null && 'then' in res) {
              res = (await res) as any;
            }
            if (res === true) {
              return;
            }
            const error = e as HttpError;
            if (error.subCode === 'finish_inspection_without_inspection_data') {
              return void alert({ title: '无法完成检测，不存在任何检测项目!' });
            }
            if (error.subCode === 'finish_empty_construction_jobs') {
              return void alert({ title: '无法完成施工，不存在任何施工项目!' });
            }
            void alert({ title: '出错了!', message: error.message });
          },
        },
      );
    },
  );

  return usePersistFn(
    <T extends ReportScreenType>(
      type: T,
      params: ViewReportRequestParams[T],
    ) => {
      const traits = getTraitsProvider(type, params);
      navigation.navigate(type as any, {
        target: params?.reportRef ?? reportTarget,
        standalone: true,
        title: params.title ?? traits.getTitle(detail),
        accessoryText: params.accessoryText ?? traits.getAccessoryText(detail),
        shareButtonRef: () => shareButtonRef,
        onShare: traits.canShare(detail, taskContext)
          ? () => void share(traits.type)
          : undefined,
        ...traits.getExtraParams(),
        secondaryAction: params.secondaryButton
          ? {
              ...params.secondaryButton,
              onPress: () => {
                params.secondaryButton?.onPress({
                  finish: async (additionalParams: AdditionalParams) => {
                    await finish(traits, additionalParams);
                  },
                });
              },
            }
          : undefined,
        action:
          params?.primaryButton ??
          (traits.canEdit(detail, taskContext)
            ? {
                text: '修改报告',
                bordered: !isMobile,
                variant: 'danger',
                onPress: () => void edit(traits),
              }
            : traits.canFinish(detail, taskContext)
            ? {
                text: params.finishButtonText ?? '完成并推送报告',
                bordered: !isMobile,
                variant: isMobile ? 'primary' : 'white',
                onPress: () =>
                  void finish(traits, params?.getAdditionalFinishParams?.()),
              }
            : undefined),
      });
    },
  );
};
